import { CUSTOMER_STAGES, type CustomerStage } from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';
import { useT } from '@/i18n/use-t';
import { Pill, cx, type PillTone } from '@/components/ui';

/**
 * Where a customer is with the store (ADR-103), said the same way everywhere:
 * the list's column and counts, the profile, a group's rules, a campaign.
 */
export const STAGE_LABELS: Record<CustomerStage, MessageKey> = {
  prospect: 'stageProspect',
  new: 'stageNew',
  active: 'stageActive',
  loyal: 'stageLoyal',
  at_risk: 'stageAtRisk',
  lost: 'stageLost',
};

export const STAGE_HINTS: Record<CustomerStage, MessageKey> = {
  prospect: 'stageProspectHint',
  new: 'stageNewHint',
  active: 'stageActiveHint',
  loyal: 'stageLoyalHint',
  at_risk: 'stageAtRiskHint',
  lost: 'stageLostHint',
};

/** Calm for the healthy stages, warm for the ones that need a call. */
export const STAGE_TONES: Record<CustomerStage, PillTone> = {
  prospect: 'neutral',
  new: 'info',
  active: 'success',
  loyal: 'success',
  at_risk: 'warning',
  lost: 'danger',
};

export function StagePill({ stage }: { stage: CustomerStage }) {
  const t = useT();
  return (
    <span title={t(STAGE_HINTS[stage])}>
      <Pill tone={STAGE_TONES[stage]}>{t(STAGE_LABELS[stage])}</Pill>
    </span>
  );
}

/** Any of the six, ticked as toggles — for a group's rules and a campaign's audience. */
export function StagesField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: CustomerStage[];
  onChange: (stages: CustomerStage[]) => void;
}) {
  const t = useT();
  const toggle = (stage: CustomerStage) =>
    onChange(CUSTOMER_STAGES.filter((entry) => (entry === stage ? !value.includes(stage) : value.includes(entry))));

  return (
    <fieldset id={id} className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium text-ink">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {CUSTOMER_STAGES.map((stage) => {
          const on = value.includes(stage);
          return (
            <button
              key={stage}
              type="button"
              aria-pressed={on}
              title={t(STAGE_HINTS[stage])}
              onClick={() => toggle(stage)}
              className={cx(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                on ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink-muted hover:border-ink-muted hover:text-ink',
              )}
            >
              {t(STAGE_LABELS[stage])}
            </button>
          );
        })}
      </div>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </fieldset>
  );
}
